import * as z from "zod";
import {
  percentChangeToWire,
  percentChangeValueSchema,
} from "./support-analytics-schemas";

/** Null when a period had nothing to compare — distinct from a 0% change, and
 * distinct from the Infinity that "grew from nothing" produces. */
const voiceComparisonSchema = z.preprocess(
  percentChangeToWire,
  percentChangeValueSchema.nullable(),
);

export const voiceAnalyticsInputSchema = z.object({
  dateRange: z.object({ start: z.date(), end: z.date() }),
});

export const voiceSegmentMetricsSchema = z.object({
  totalCalls: z.number(),
  connectedCalls: z.number(),
  missedCalls: z.number(),
  voicemailCalls: z.number(),
  /** Still ringing when the search ran; excluded from `answerRate`. */
  ringingCalls: z.number(),
  answerRate: z.number(),
  /** Null when no call contributed — not the same as an instant pickup. */
  avgTimeToAnswerSeconds: z.number().nullable(),
  avgTalkTimeSeconds: z.number().nullable(),
  avgTimeToAbandonSeconds: z.number().nullable(),
  callsWithRecording: z.number(),
  callsWithTranscript: z.number(),
});

/** Headline movement vs. the previous period, on the overall segment. */
export const voiceComparisonsSchema = z.object({
  totalCalls: voiceComparisonSchema,
  answerRate: voiceComparisonSchema,
  avgTalkTimeSeconds: voiceComparisonSchema,
  avgTimeToAnswerSeconds: voiceComparisonSchema,
  avgTimeToAbandonSeconds: voiceComparisonSchema,
});

/** One row per agent who *placed* an outbound call. The index's `agentId` is
 * the message sender, which is the customer on every inbound call. */
export const voiceAgentMetricsSchema = z.object({
  agentId: z.string(),
  name: z.string(),
  email: z.string(),
  calls: z.number(),
  connectedCalls: z.number(),
  avgTalkTimeSeconds: z.number().nullable(),
});

export const voiceAnalyticsOutputSchema = z.object({
  overall: voiceSegmentMetricsSchema,
  inbound: voiceSegmentMetricsSchema,
  outbound: voiceSegmentMetricsSchema,
  comparisons: voiceComparisonsSchema,
  perAgent: z.array(voiceAgentMetricsSchema),
});

export type VoiceAnalyticsInput = z.infer<typeof voiceAnalyticsInputSchema>;
export type VoiceAnalyticsOutput = z.infer<typeof voiceAnalyticsOutputSchema>;
export type VoiceSegmentMetrics = z.infer<typeof voiceSegmentMetricsSchema>;
export type VoiceAgentMetrics = z.infer<typeof voiceAgentMetricsSchema>;
export type VoiceComparisons = z.infer<typeof voiceComparisonsSchema>;
